/**
 * Normalize a URL to ensure it's resolved from the root domain
 * Handles both relative URLs (for navigation) and absolute URLs (for window.open)
 * @param {string} url - The URL to normalize
 * @returns {string} The normalized URL
 */
export const normalizeUrl = (url) => {
  if (!url) return url;

  // If URL is already absolute (http:// or https://), return as is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  // If URL already starts with '/', return as is (already resolved from root)
  if (url.startsWith('/')) {
    return url;
  }

  // Otherwise, prefix with '/' to resolve from root
  return `/${url}`;
};

/**
 * Extract all query parameters from URL and convert to JSON object
 * @param {string} [search] - Optional search string (defaults to window.location.search)
 * @returns {Record<string, string> | null} - All query parameters as an object or null
 */
export const extractAllParamsFromUrl = (search) => {
  const searchString = search || (typeof window !== 'undefined' ? window.location.search : '');

  if (!searchString || !searchString.startsWith('?')) {
    return null;
  }

  // Remove the leading '?' and parse query parameters
  const params = new URLSearchParams(searchString.substring(1));

  // Convert URLSearchParams to plain object using Array.from
  /** @type {Record<string, string>} */
  const paramsObj = {};
  Array.from(params.entries()).forEach(([key, value]) => {
    paramsObj[key] = value;
  });

  return Object.keys(paramsObj).length > 0 ? paramsObj : null;
};

/** URL query params used only for SPA UI state (not sent as API `contexte`). */
export const CONTEXT_EXCLUDED_QUERY_PARAMS = ['page'];

/**
 * Remove UI-only query params before building API `contexte`.
 * @param {Record<string, string> | null} paramsObj - Raw query parameters
 * @returns {Record<string, string> | null} Filtered parameters, or null when empty
 */
export const omitExcludedContextParams = (paramsObj) => {
  if (!paramsObj) {
    return null;
  }

  const excluded = new Set(CONTEXT_EXCLUDED_QUERY_PARAMS);
  /** @type {Record<string, string>} */
  const filtered = {};

  Object.entries(paramsObj).forEach(([key, value]) => {
    if (!excluded.has(key)) {
      filtered[key] = value;
    }
  });

  return Object.keys(filtered).length > 0 ? filtered : null;
};

/**
 * Build API `contexte` JSON from a URL search string (e.g. `?id=1&page=2` → `{"id":"1"}`).
 * @param {string} [search] - Search string with leading `?`, or empty
 * @returns {string | null} JSON string for API calls, or null when no business context remains
 */
export const buildContexteFromUrlSearch = (search) => {
  const paramsObj = omitExcludedContextParams(extractAllParamsFromUrl(search));
  return paramsObj ? JSON.stringify(paramsObj) : null;
};

/**
 * Build a detail-tab navigation URL.
 * If `targetPath` already includes a query string, return it as-is (API leaf paths
 * are self-contained). Otherwise append the current search params (preserve context).
 * @param {string} targetPath - Tab path from the API (with or without query)
 * @param {URLSearchParams | { toString: () => string } | null | undefined} searchParams
 * @returns {string}
 */
export const buildDetailNavUrl = (targetPath, searchParams) => {
  const normalized = normalizeUrl(targetPath);
  if (!normalized) return normalized;

  if (normalized.includes('?')) {
    return normalized;
  }

  const searchString = searchParams?.toString() ?? '';
  return searchString ? `${normalized}?${searchString}` : normalized;
};

/**
 * Split a path into pathname + query (hash ignored). Leading `/` stripped from pathname.
 * @param {string} path
 * @returns {{ pathname: string, query: string }}
 */
const splitPathAndQuery = (path) => {
  const withoutHash = (path || '').split('#')[0];
  const [rawPathname = '', query = ''] = withoutHash.split('?');
  return {
    pathname: rawPathname.replace(/^\//, ''),
    query,
  };
};

/**
 * Whether the current browser path matches a detail tab path.
 * Pathnames must equal. If the tab path has a query, every of its params must be
 * present on the current path (extra UI params like `page` are allowed).
 * @param {string} currentPath - Current location (pathname + search), e.g. `/foo.php?a=1`
 * @param {string | null | undefined} tabPath - Tab item path from the API
 * @returns {boolean}
 */
export const detailTabPathMatches = (currentPath, tabPath) => {
  if (!tabPath || !currentPath) return false;

  const current = splitPathAndQuery(currentPath);
  const tab = splitPathAndQuery(tabPath);

  if (current.pathname !== tab.pathname) return false;
  if (!tab.query) return true;

  const currentParams = new URLSearchParams(current.query);
  const tabParams = new URLSearchParams(tab.query);

  return Array.from(tabParams.entries()).every(
    ([key, value]) => currentParams.get(key) === value,
  );
};

/**
 * Append or replace the `contexte` query parameter on a URL.
 * Set-attribut requires this query param even when the page has no context.
 * @param {string} url - The URL to modify
 * @param {string | null | undefined} contexte - JSON string from useContexte, or null
 * @returns {string} The URL with a `contexte` query parameter
 */
export const appendContexteToUrl = (url, contexte) => {
  if (!url) return url;

  const [basePath, queryString] = url.split('?');
  const params = new URLSearchParams(queryString || '');
  params.set('contexte', contexte ?? '{}');

  return `${basePath}?${params.toString()}`;
};

/**
 * Merge key/value pairs into the `contexte` JSON query param of a URL.
 * @param {string} url - The URL to modify
 * @param {Record<string, unknown>} values - Keys to merge into contexte
 * @returns {string}
 */
export const appendContexteValuesToUrl = (url, values) => {
  if (!url) return url;

  const [basePath, queryString] = url.split('?');
  const params = new URLSearchParams(queryString || '');

  /** @type {Record<string, unknown>} */
  let contexte = {};
  if (params.has('contexte')) {
    try {
      contexte = JSON.parse(params.get('contexte') ?? '{}');
    } catch {
      contexte = {};
    }
  }

  Object.entries(values).forEach(([key, value]) => {
    contexte[key] = value;
  });
  params.set('contexte', JSON.stringify(contexte));

  return `${basePath}?${params.toString()}`;
};

/**
 * Append selectedIds to the `contexte` query parameter of a URL
 * If `contexte` already exists, merges selectedIds into it; otherwise creates it.
 * @param {string} url - The URL to modify
 * @param {string[]} selectedIds - The selected row IDs to inject
 * @returns {string} The URL with selectedIds in the contexte parameter
 */
export const appendSelectedIdsToUrl = (url, selectedIds) => (
  appendContexteValuesToUrl(url, { selectedIds })
);
